import React, { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Id } from "../../../../../../convex/_generated/dataModel";
import { useToast } from "@/components/ui/use-toast";

interface AddEquipmentToLocationSheetProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: Id<"serviceLocations">;
}

export function AddEquipmentToLocationSheet({
  isOpen,
  onClose,
  locationId,
}: AddEquipmentToLocationSheetProps) {
  const [selectedEquipmentId, setSelectedEquipmentId] = useState("");
  const [assignmentNotes, setAssignmentNotes] = useState("");
  const { toast } = useToast();

  const allEquipment = useQuery(api.equipment.list) || [];
  const assignEquipment = useMutation(api.equipment.assignToLocation);

  // Filter out equipment that is already assigned to a location
  const availableEquipment = allEquipment.filter((item: any) => !item.location);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedEquipmentId) {
      toast({
        title: "Error",
        description: "Please select equipment to assign",
        variant: "destructive",
      });
      return;
    }

    try {
      await assignEquipment({
        equipmentId: selectedEquipmentId as Id<"equipment">,
        locationId: locationId,
        notes: assignmentNotes || undefined,
      });

      toast({
        title: "Success!",
        description: "Equipment assigned successfully",
      });
      setSelectedEquipmentId("");
      setAssignmentNotes("");
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to assign equipment",
        variant: "destructive",
      });
    }
  };

  const selectedEquipment = availableEquipment.find(
    (item: any) => item._id === selectedEquipmentId
  );

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[500px] sm:max-w-[500px]">
        <SheetHeader>
          <SheetTitle>Assign Equipment to Location</SheetTitle>
          <SheetDescription>Select equipment to assign to this location.</SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-180px)] pr-4">
          <form onSubmit={handleSubmit} className="space-y-6 px-2 py-6">
            <div className="space-y-2">
              <Label htmlFor="equipmentId">
                Equipment <span className="text-red-500">*</span>
              </Label>
              <Select value={selectedEquipmentId} onValueChange={setSelectedEquipmentId} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select equipment to assign" />
                </SelectTrigger>
                <SelectContent>
                  {availableEquipment.length === 0 ? (
                    <SelectItem value="none" disabled>
                      No unassigned equipment available
                    </SelectItem>
                  ) : (
                    availableEquipment.map((item: any) => (
                      <SelectItem key={item._id} value={item._id}>
                        {item.name} - {item.brand} {item.model}
                        {item.serialNumber && ` (${item.serialNumber})`}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {selectedEquipment && (
              <div className="rounded-lg bg-gray-50 p-4">
                <h3 className="mb-2 font-medium">Equipment Details</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Brand:</span> {selectedEquipment.brand}
                  </div>
                  <div>
                    <span className="text-gray-500">Model:</span> {selectedEquipment.model}
                  </div>
                  <div>
                    <span className="text-gray-500">Serial Number:</span>{" "}
                    {selectedEquipment.serialNumber || "-"}
                  </div>
                  <div>
                    <span className="text-gray-500">Status:</span> {selectedEquipment.status}
                  </div>
                  <div>
                    <span className="text-gray-500">Purchase Date:</span>{" "}
                    {new Date(selectedEquipment.purchaseDate).toLocaleDateString()}
                  </div>
                  <div>
                    <span className="text-gray-500">Purchase Price:</span>{" "}
                    {selectedEquipment.purchasePrice
                      ? new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                        }).format(selectedEquipment.purchasePrice)
                      : "-"}
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="assignmentNotes">Assignment Notes</Label>
              <Input
                id="assignmentNotes"
                value={assignmentNotes}
                onChange={(e) => setAssignmentNotes(e.target.value)}
                placeholder="Optional notes about this assignment"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                className="flex-1"
                disabled={!selectedEquipmentId || availableEquipment.length === 0}
              >
                Assign Equipment
              </Button>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </form>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
